import React, { Component, Fragment } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import styled from 'styled-components'
import { media, style } from 'app/assets/styles'
import { colors as oldColors } from 'shared/assets/styles'
import { colors } from 'app-v2/design-systems/theme'
import { CaptionText, H4, H5, Text, TinyText } from 'app/common/typography'
import type { CookiesConsents } from 'shared/store/user'
import { deleteCookies } from 'shared/utils'
import type { IntlShape } from 'react-intl'
import { Link } from 'app/common/link'

const { yellow } = oldColors

type Props = {
    cookiesConsents: Array<{
        id: string,
        title: string,
        description: string,
        consent?: boolean,
        disabled?: boolean,
        checked?: boolean
    }>,
    open: boolean,
    manageCookies: boolean,
    cookiesConsentsDone: boolean,
    closeManageCookies: () => void,
    handleClose: Function,
    consentCookies: CookiesConsents => void,
    intl: IntlShape,
    className?: string
}

type State = {
    temporaryCookiesConsents: CookiesConsents
}

class CookiesManagement extends Component<Props, State> {
    constructor(props: Props) {
        super()
        const temporaryCookiesConsents = {
            media: false,
            technical: true,
            tracking: false
        }
        const { cookiesConsentsDone, cookiesConsents } = props
        if (cookiesConsentsDone) {
            cookiesConsents.forEach(element => {
                temporaryCookiesConsents[element.id] = element.consent
            })
        }
        this.state = {
            temporaryCookiesConsents: temporaryCookiesConsents
        }
    }

    sendCookiesConsentData = () => {
        const { consentCookies } = this.props
        const { temporaryCookiesConsents } = this.state
        /* Delete all cookies because tracking is not allowed by user */
        !temporaryCookiesConsents.tracking && deleteCookies()
        consentCookies(temporaryCookiesConsents)
    }

    toggleCookieConsent(id: string) {
        const cookiesConsents = this.state.temporaryCookiesConsents
        cookiesConsents[id] = !cookiesConsents[id]
        this.setState({
            temporaryCookiesConsents: cookiesConsents
        })
    }

    render() {
        const { cookiesConsents, intl, className } = this.props
        const consents = cookiesConsents.map((cookieConsent, index) => {
            const {
                id,
                title,
                description,
                disabled,
                ...otherProps
            } = cookieConsent
            const consent = this.state.temporaryCookiesConsents[id]
            return (
                <Row key={index}>
                    <CookieTitle className={className}>
                        {disabled ? (
                            <Label>
                                {intl.formatMessage({
                                    id: title
                                })}
                                <FormattedMessage
                                    id={
                                        'cookies.management.enabled.obligatoire'
                                    }>
                                    {(text: string) => (
                                        <CookieStatus consent={consent}>
                                            {text}
                                        </CookieStatus>
                                    )}
                                </FormattedMessage>
                            </Label>
                        ) : (
                            <Fragment>
                                <Toggle
                                    id={id}
                                    type="checkbox"
                                    checked={consent}
                                    onChange={() =>
                                        this.toggleCookieConsent(id)
                                    }
                                    {...otherProps}
                                />
                                <LabelForInput htmlFor={id}>
                                    {intl.formatMessage({
                                        id: title
                                    })}
                                    <FormattedMessage
                                        id={
                                            consent
                                                ? 'cookies.management.enabled'
                                                : 'cookies.management.disabled'
                                        }>
                                        {(text: string) => (
                                            <CookieStatus consent={consent}>
                                                {text}
                                            </CookieStatus>
                                        )}
                                    </FormattedMessage>
                                </LabelForInput>
                            </Fragment>
                        )}
                    </CookieTitle>
                    <FormattedMessage id={description}>
                        {(text: string) =>
                            className === 'cookies-screen' ? (
                                <Text>{text}</Text>
                            ) : (
                                <CookieDescription>{text}</CookieDescription>
                            )
                        }
                    </FormattedMessage>
                </Row>
            )
        })

        return (
            <Fragment>
                <Row>
                    <FormattedMessage id="cookies.management.help">
                        {(text: string) =>
                            className === 'cookies-screen' ? (
                                <Text>{text}</Text>
                            ) : (
                                <CaptionText>{text}</CaptionText>
                            )
                        }
                    </FormattedMessage>
                </Row>

                <FormattedMessage id="cookies.management.types">
                    {(text: string) => (
                        <StyledH4 className={className}>{text}</StyledH4>
                    )}
                </FormattedMessage>

                <Consents>{consents}</Consents>
                <StyledLink
                    background={yellow}
                    border={yellow}
                    className={className}
                    onClick={this.sendCookiesConsentData}
                    url="/">
                    <FormattedMessage id="cookies.consent.accept" />
                </StyledLink>
            </Fragment>
        )
    }
}

const Row = styled.div`
    margin: 20px 0;
    position: relative;
`

const Toggle = styled.input`
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    &:disabled + label {
        &:before,
        &:after {
            opacity: 0.8;
        }
    }
    &:checked + label {
        &:before {
            transform: translateX(18px);
        }
        &:after {
            background: ${colors.crunchyGreen};
        }
    }
}
`
const Label = styled.label`
    display: inline-block;
`

const LabelForInput = styled(Label)`
    cursor: pointer;

    &:before {
        transition: transform 0.3s ease-in-out;
        content: '';
        position: absolute;
        top: 5px;
        right: 20px;
        z-index: 1;
        display: block;
        width: 14px;
        height: 14px;
        background: #fff;
        border-radius: 50%;
    }

    &:after {
        transition: background 0.3s ease-in-out;
        content: '';
        position: absolute;
        top: 3px;
        right: 0;
        display: block;
        width: 36px;
        height: 18px;
        background: #999;
        border-radius: 18px;
    }
`

const Consents = styled.div`
    display: flex;
    flex-direction: column;
    ${media.medium`
        flex-direction: row;
        ${Row} {
            flex: 1;
            margin: 5px 0 10px 40px;
            &:first-of-type {
                margin-left: 0;
            }
        }
    `};
`

const CookieTitle = styled(H5)`
    display: flex;
    align-items: center;
    border-bottom: solid 1px ${oldColors.black40};
    ${media.medium`
        font-size: 18px;
    `};
    margin: 0;
`

const CookieStatus = styled(TinyText)`
    margin: 0 0.5em;
    vertical-align: middle;
    color: ${props => (props.consent ? 'inherit' : oldColors.grey30)};
`

const CookieDescription = styled(TinyText)`
    ${media.medium`
        font-size: 14px;
    `};
`

const StyledH4 = styled(H4)`
    margin: 0;
    &.cookies-screen {
        margin-top: 35px;
        ${media.medium`
            margin-top: 60px;
            margin-bottom: 40px;
        `}
    }
`

const StyledLink = styled(Link)`
    ${style.buttonSmall}
    float: right;
    &.cookies-screen {
        float: unset;
        position: relative;
        margin-top: 25px;
        margin-bottom: 25px;
        left: 50%;
        -ms-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
        ${media.xsmall`
            ${style.buttonLarge};
            margin-top: 50px;
        `}
        ${media.small`
            margin-top: 100px;
            margin-bottom: 50px;
        `}
    }
`

export const CookiesManagementComponent = injectIntl(CookiesManagement)
